/* ============================================================================
 * jrs-v2-cart-delivery-bar.js — Cart free-delivery threshold bar (v2 stack)
 *
 * Stage 2: pure helpers — formatMoney (SSR/CSR parity), gapState, pctOfThreshold.
 * Stage 3: hydration. Adopts the SSR mount (snippets/jrs-delivery-bar.liquid) and
 *   re-renders it on cart:updated — unlock morph, exclusion recompute, a polite
 *   live region, and FR7 telemetry. DOM writes go through the data-jrs-fdb-* hooks:
 *   a STATE change does a full re-render; a same-state amount change updates the
 *   hooks only, so the fill's width transition animates instead of snapping.
 *
 * Contracts:
 *   - JRS.EventBus.on('cart:updated', fn) -> fn({ cart })   (full /cart.js, pence)
 *   - JRS.i18n.t('jrs_delivery_bar.<key>', params)          ({name} interpolation)
 *   - JRS.Analytics.fireAnalyticsEvent(name, payload)       -> window.dataLayer
 *   - SSR cart blob [data-jrs-cart-json] (cart-main.liquid) for the initial total
 *
 * The money formatter is the SSR/CSR parity contract (Stage 2 directive 3): it must
 * equal Liquid `| money` byte-for-byte. Verified live: 672 -> "£6.72", 11280 ->
 * "£112.80", 15000 -> "£150.00", 0 -> "£0.00", 118860 -> "£1,188.60". Grouping is
 * manual (not toLocaleString) so it is identical regardless of the engine's ICU.
 *
 * Per-bar state lives on the mount (mount._fdb*), so the module is stateless and
 * re-entrant. Loaded on the cart tier only, deferred, after jrs-v2-cart-api.js.
 *
 * IIFE on window.JRS — ES5-compatible, no bundler, per repo convention.
 * ========================================================================== */
(function () {
  'use strict';

  var JRS = (window.JRS = window.JRS || {});
  if (JRS.DeliveryBar && JRS.DeliveryBar._stage3) { return; } // idempotent

  // Icons — byte-match the SSR snippet so a full re-render is visually identical.
  var ICON_VAN = '<span class="jrs-delivery-bar__icon" aria-hidden="true">' +
    '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 6h13v9H1zM14 9h4l3 3v3h-7z"/><circle cx="5.5" cy="17.5" r="1.6"/><circle cx="17.5" cy="17.5" r="1.6"/></svg></span>';
  var ICON_CHECK = '<span class="jrs-delivery-bar__icon" aria-hidden="true">' +
    '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></span>';

  // =====================================================================
  // Pure helpers (Stage 2) — also the SSR/CSR parity contract.
  // =====================================================================
  function formatMoney(pence) {
    var p = Math.round(Number(pence));
    if (!isFinite(p)) { p = 0; }
    var neg = p < 0;
    p = Math.abs(p);
    var pounds = Math.floor(p / 100);
    var rem = p % 100;
    var dec = rem < 10 ? '0' + rem : '' + rem;
    var s = '' + pounds;
    var grouped = '';
    while (s.length > 3) {
      grouped = ',' + s.slice(s.length - 3) + grouped;
      s = s.slice(0, s.length - 3);
    }
    grouped = s + grouped;
    return (neg ? '-£' : '£') + grouped + '.' + dec;
  }

  function remainingPence(cartExVat, thresholdPence) {
    return thresholdPence - cartExVat;
  }

  function gapState(remaining, closePence, cutlinePence) {
    if (remaining <= 0) { return 'unlocked'; }
    if (remaining <= closePence) { return 'close'; }
    if (remaining <= cutlinePence) { return 'approaching'; }
    return 'far';
  }

  function pctOfThreshold(cartExVat, thresholdPence) {
    if (!thresholdPence) { return 0; }
    var pct = (cartExVat * 100) / thresholdPence;
    if (pct > 100) { pct = 100; }
    if (pct < 0) { pct = 0; }
    return pct;
  }

  // =====================================================================
  // i18n + telemetry shims (fail-silent)
  // =====================================================================
  function i18n(key, params) {
    if (JRS.i18n && typeof JRS.i18n.t === 'function') {
      return JRS.i18n.t('jrs_delivery_bar.' + key, params);
    }
    return '';
  }

  function fire(name, payload) {
    try {
      if (JRS.Analytics && typeof JRS.Analytics.fireAnalyticsEvent === 'function') {
        JRS.Analytics.fireAnalyticsEvent(name, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var o = { event: name }, k;
        for (k in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, k)) { o[k] = payload[k]; }
        }
        window.dataLayer.push(o);
      }
    } catch (e) { /* fail-silent per Doc 3 §8.3 — never block the cart */ }
  }

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }
  }

  // =====================================================================
  // VAT display basis (STACK observation 20 — the bar was toggle-blind).
  // Mirrors the sitewide toggle: sessionStorage 'exclVATv3' where 'false' =
  // inc VAT and 'true'/absent = exc VAT (the configured default, state_2).
  // Internal maths stays in ex-VAT pence — the ASR rule basis — and only the
  // DISPLAYED amounts re-express at ×1.2 in inc mode, so state boundaries
  // (unlocked/close/approaching/far) are identical in both modes.
  // =====================================================================
  function vatMode() {
    try {
      if (sessionStorage.getItem('exclVATv3') === 'false') { return 'inc'; }
    } catch (e) { /* storage unavailable → default */ }
    return 'exc';
  }
  function dispPence(pence) {
    return vatMode() === 'inc' ? Math.round(pence * 1.2) : pence;
  }
  function msgKey(base) {
    if (vatMode() !== 'inc') { return base; }
    // Shopify escapes translation values unless the key ENDS in _html, so the
    // markup-bearing inc variants are named *_inc_html (e.g. close_inc_html).
    return /_html$/.test(base) ? base.replace(/_html$/, '_inc_html') : base + '_inc';
  }
  function thresholdDisplayFor(settings) {
    if (vatMode() !== 'inc') { return settings.thresholdDisplay; }
    return formatMoney(Math.round(settings.threshold * 1.2)).replace(/\.00$/, '');
  }

  // =====================================================================
  // Settings + cart reading
  // =====================================================================
  function readSettings(mount) {
    function num(attr, dflt, minVal) {
      var v = parseInt(mount.getAttribute(attr), 10);
      if (isNaN(v)) { return dflt; }
      if (minVal != null && v < minVal) { return dflt; } // guard: threshold 0/negative → fallback
      return v;
    }
    return {
      threshold: num('data-jrs-fdb-threshold', 12500, 1), // <=0 → fallback (avoid divide-by-zero)
      cutline: num('data-jrs-fdb-cutline', 5000),
      close: num('data-jrs-fdb-close', 2000),
      morphMs: num('data-jrs-fdb-morph', 750),
      thresholdDisplay: mount.getAttribute('data-jrs-fdb-threshold-display') || '£125',
      delivery: mount.getAttribute('data-jrs-fdb-delivery') || '£5.79'
    };
  }

  function readInitialCart() {
    var blob = document.querySelector('[data-jrs-cart-json]');
    if (blob) { try { return JSON.parse(blob.textContent); } catch (e) {} }
    return null;
  }

  // =====================================================================
  // Exclusion recompute (directive 3) — intersect the SSR'd excluded-ID set with
  // the live cart's product_ids. Fail-safe: a non-empty-but-unparseable attribute,
  // or an absent attribute alongside the SSR suppressed marker, stays suppressed.
  // =====================================================================
  function computeSuppressed(mount, cart) {
    var raw = mount.getAttribute('data-jrs-fdb-excluded-ids');
    var marker = mount.getAttribute('data-jrs-fdb-suppressed') === 'true';
    if (raw === null) { return marker; }
    raw = ('' + raw).replace(/^\s+|\s+$/g, '');
    if (raw === '') { return false; }
    var ids = {}, parsed = 0, parts = raw.split(','), i, n;
    for (i = 0; i < parts.length; i++) {
      n = parseInt(parts[i], 10);
      if (!isNaN(n)) { ids[n] = true; parsed++; }
    }
    if (parsed === 0) { return true; }
    var items = (cart && cart.items) || [];
    for (i = 0; i < items.length; i++) {
      if (ids[items[i].product_id]) { return true; }
    }
    return false;
  }

  // =====================================================================
  // Markup builders
  // =====================================================================
  function ariaLabel(settings) {
    return i18n(msgKey('aria_progress'), { threshold: thresholdDisplayFor(settings) });
  }

  function fullRender(mount, settings, total, state) {
    var remaining = Math.max(0, settings.threshold - total);
    var pct = pctOfThreshold(total, settings.threshold);
    mount.className = 'jrs-delivery-bar jrs-delivery-bar--' + state;
    if (state === 'unlocked') { mount.setAttribute('role', 'status'); } else { mount.removeAttribute('role'); }

    mount._fdbVatMode = vatMode();
    var html;
    if (state === 'far') {
      html = ICON_VAN + '<span class="jrs-delivery-bar__msg">' +
        i18n(msgKey('far_html'), { threshold: thresholdDisplayFor(settings) }) + '</span>';
    } else if (state === 'unlocked') {
      html = ICON_CHECK + '<span class="jrs-delivery-bar__msg">' +
        i18n(msgKey('unlocked'), { threshold: thresholdDisplayFor(settings) }) + '</span>';
    } else if (state === 'unlocking') {
      html = '<div class="jrs-delivery-bar__row"><span class="jrs-delivery-bar__msg">' +
        i18n('unlocking', {}) + '</span></div>' +
        '<div class="jrs-delivery-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="' + settings.threshold +
        '" aria-valuenow="' + settings.threshold + '" aria-label="' + ariaLabel(settings) + '">' +
        '<div class="jrs-delivery-bar__fill" data-jrs-fdb-fill style="width:100%"></div></div>';
    } else {
      var key = (state === 'close') ? 'close_html' : 'approaching_html';
      html = '<div class="jrs-delivery-bar__row"><span class="jrs-delivery-bar__msg">' +
        i18n(msgKey(key), { amount: formatMoney(dispPence(remaining)) }) + '</span>' +
        '<span class="jrs-delivery-bar__pct" data-jrs-fdb-pct>' + Math.round(pct) + '%</span></div>' +
        '<div class="jrs-delivery-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="' + settings.threshold +
        '" aria-valuenow="' + total + '" aria-label="' + ariaLabel(settings) + '">' +
        '<div class="jrs-delivery-bar__fill" data-jrs-fdb-fill style="width:' + pct.toFixed(1) + '%"></div></div>' +
        '<span class="jrs-delivery-bar__sr" data-jrs-fdb-sr aria-live="polite"></span>';
    }
    mount.innerHTML = html;
  }

  function updateHooks(mount, settings, total) {
    var remaining = Math.max(0, settings.threshold - total);
    var pct = pctOfThreshold(total, settings.threshold);
    var amt = mount.querySelector('[data-jrs-fdb-amount]');
    if (amt) { amt.textContent = formatMoney(dispPence(remaining)); }
    var pctEl = mount.querySelector('[data-jrs-fdb-pct]');
    if (pctEl) { pctEl.textContent = Math.round(pct) + '%'; }
    var fill = mount.querySelector('[data-jrs-fdb-fill]');
    if (fill) { fill.style.width = pct.toFixed(1) + '%'; }
    var track = mount.querySelector('[role="progressbar"]');
    if (track) { track.setAttribute('aria-valuenow', total); }
  }

  function applyState(mount, settings, total, state) {
    if (mount._fdbState !== state || mount._fdbVatMode !== vatMode()) {
      fullRender(mount, settings, total, state);
      mount._fdbState = state;
    } else {
      updateHooks(mount, settings, total); // same state -> hooks only (keeps the fill transition)
    }
  }

  function updateLiveRegion(mount, settings, state, remaining) {
    var sr = mount.querySelector('[data-jrs-fdb-sr]');
    if (!sr) { return; } // only progress states carry the live region
    var amountStr = formatMoney(dispPence(Math.max(0, remaining)));
    var key = state + '|' + amountStr;
    if (mount._fdbLastSr === key) { return; } // unchanged -> do not re-announce
    mount._fdbLastSr = key;
    sr.textContent = i18n(msgKey('sr_remaining'), { amount: amountStr });
  }

  // =====================================================================
  // Render (cart:updated) — last-event-wins, idempotent, morph-aware
  // =====================================================================
  function clearMorph(mount) {
    if (mount._fdbMorphTimer) { clearTimeout(mount._fdbMorphTimer); mount._fdbMorphTimer = null; }
  }

  function fireImpression(mount, state, remaining) {
    if (mount._fdbImpressionFired) { return; }
    mount._fdbImpressionFired = true;
    fire('jrs_delivery_bar_impression', { bar_state: state, remaining_pence: Math.max(0, remaining) });
  }

  function render(mount, settings, cart, opts) {
    opts = opts || {};
    var allowMorph = opts.allowMorph !== false; // first paint passes { allowMorph: false }
    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : 0;
    var itemCount = cart ? (cart.item_count != null ? cart.item_count : (cart.items ? cart.items.length : 0)) : 0;

    clearMorph(mount);

    // empty cart (e.g. last item removed) -> hide
    if (!cart || itemCount === 0 || total <= 0) {
      mount.hidden = true;
      mount._fdbSuppressed = true;
      return;
    }
    // exclusion recompute
    if (computeSuppressed(mount, cart)) {
      mount.hidden = true;
      mount._fdbSuppressed = true;
      return;
    }
    var wasHidden = mount._fdbSuppressed || mount.hidden;
    mount._fdbSuppressed = false;
    if (mount.hidden) { mount.hidden = false; }

    var remaining = settings.threshold - total;
    var state = gapState(remaining, settings.close, settings.cutline);
    var justUnlocked = (state === 'unlocked' && !mount._fdbPrevUnlocked);
    // Revealing from a hidden state is not a watched transition: no morph, no unlock event.
    var transition = allowMorph && !wasHidden;

    if (justUnlocked && transition && !prefersReducedMotion()) {
      fullRender(mount, settings, total, 'unlocking');
      mount._fdbState = 'unlocking';
      mount._fdbMorphTimer = setTimeout(function () {
        fullRender(mount, settings, total, 'unlocked');
        mount._fdbState = 'unlocked';
        mount._fdbMorphTimer = null;
        updateLiveRegion(mount, settings, 'unlocked', 0);
      }, settings.morphMs);
    } else {
      applyState(mount, settings, total, state);
    }
    mount._fdbPrevUnlocked = (state === 'unlocked');

    // FR7 — unlocked on a true transition (fires even under reduced motion; decoupled from the animation)
    if (justUnlocked && transition) {
      fire('jrs_delivery_bar_unlocked', { bar_state: 'unlocked', remaining_pence: 0 });
    }
    // deferred impression (was suppressed on first paint, now visible)
    if (mount._fdbImpressionPending) {
      mount._fdbImpressionPending = false;
      fireImpression(mount, state, Math.max(0, remaining));
    }
    updateLiveRegion(mount, settings, state, remaining);
  }

  // =====================================================================
  // Hydrate — adopt the SSR markup (no first-paint re-render / morph), fire the
  // once-per-view impression, and subscribe to cart:updated.
  // =====================================================================
  function hydrate(mount) {
    if (!mount || mount._fdbHydrated) { return; }
    mount._fdbHydrated = true;
    var settings = readSettings(mount);
    var state = mount.getAttribute('data-jrs-fdb-state') || mount._fdbState || '';
    mount._fdbState = state;
    mount._fdbPrevUnlocked = (state === 'unlocked');
    var suppressed = mount.hasAttribute('hidden');
    mount._fdbSuppressed = suppressed;

    var cart = readInitialCart();
    var total = (cart && typeof cart.total_price === 'number') ? cart.total_price : null;
    var remaining = (total != null) ? (settings.threshold - total) : 0;
    mount._fdbLastTotal = total;
    mount._fdbVatMode = 'exc'; // SSR always renders the ex-VAT basis

    if (!suppressed) {
      fireImpression(mount, state, Math.max(0, remaining));
    } else {
      mount._fdbImpressionPending = true; // fire when it becomes visible
    }

    // SSR is ex-basis; if the shopper's toggle is already on inc VAT, re-express
    // the adopted markup once (state is basis-independent, so no morph/telemetry).
    if (!suppressed && total != null && state && state !== 'unlocking' && vatMode() === 'inc') {
      fullRender(mount, settings, total, state);
    }

    // Re-express the displayed basis whenever the sitewide VAT toggle changes.
    document.addEventListener('vatToggleChange', function () {
      if (mount._fdbSuppressed || mount.hidden) { return; }
      var t = mount._fdbLastTotal;
      if (t == null || mount._fdbVatMode === vatMode()) { return; }
      var st = gapState(settings.threshold - t, settings.close, settings.cutline);
      fullRender(mount, settings, t, st);
      mount._fdbState = st;
      updateLiveRegion(mount, settings, st, settings.threshold - t);
    });

    if (JRS.EventBus && typeof JRS.EventBus.on === 'function') {
      JRS.EventBus.on('cart:updated', function (data) {
        // Re-resolve the mount (2026-09-02 fix): the bar lives inside the
        // [data-jrs-cart-items] container that the cart page's
        // _refreshCartSection() innerHTML-swaps after removals / quantity
        // changes — the captured node is then detached and renders would go
        // nowhere. Adopt the live node, carrying over the per-mount state so
        // the impression event never re-fires for the same view.
        var live = document.querySelector('[data-jrs-delivery-bar]');
        if (live && live !== mount) {
          live._fdbImpressionFired = mount._fdbImpressionFired;
          live._fdbLastTotal = mount._fdbLastTotal;
          live._fdbMorphState = mount._fdbMorphState;
          mount = live;
        }
        var c = data && data.cart;
        if (c && typeof c.total_price === 'number') { mount._fdbLastTotal = c.total_price; }
        render(mount, settings, c, { allowMorph: true });
      });
    }
  }

  function autoInit() {
    var mount = document.querySelector('[data-jrs-delivery-bar]');
    if (mount) { hydrate(mount); }
  }

  // =====================================================================
  // Export (public helpers + hydration surface / test seam)
  // =====================================================================
  JRS.DeliveryBar = {
    _stage3: true,
    formatMoney: formatMoney,
    remainingPence: remainingPence,
    gapState: gapState,
    pctOfThreshold: pctOfThreshold,
    hydrate: hydrate,
    render: render,
    readSettings: readSettings,
    computeSuppressed: computeSuppressed,
    fullRender: fullRender
  };

  // Auto-init on the cart page. Guarded so the module loads cleanly in a no-DOM
  // sandbox (the pure-helper parity test). Wait for DOMContentLoaded so the later-
  // deferred Analytics module is registered before the once-per-view impression fires
  // (fire() falls back to dataLayer otherwise).
  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'complete') {
      autoInit();
    } else {
      document.addEventListener('DOMContentLoaded', autoInit);
    }
  }
})();
